import React, { useState } from 'react';

export interface HistogramBinView {
  from: number;
  to: number | null;
  count: number;
  /** highlight this bin as inside the user's preferred range. */
  inRange?: boolean;
}

interface HistogramProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  bins: HistogramBinView[];
  /** appended to the axis end label, e.g. "cm" / "mi". */
  unit?: string;
  /** shown under the chart, e.g. "25% in range". */
  stat?: React.ReactNode;
  /** render a "soft" pill next to the title (height = doesn't gate). */
  soft?: boolean;
  /**
   * Color every bar as the primary distribution (no preferred-range concept) —
   * used by the admin pool view, where all bars should read clearly rather than
   * being muted. When false (default), only `inRange` bars are emphasized.
   */
  emphasizeAll?: boolean;
  barHeight?: number;
}

/**
 * A compact vertical-bar histogram. Bars flagged `inRange` render in the accent
 * color (the user's preferred band); the rest are muted. Pure presentational —
 * the caller supplies pre-binned counts.
 */
export function Histogram({
  title,
  subtitle,
  bins,
  unit,
  stat,
  soft,
  emphasizeAll,
  barHeight = 72,
}: HistogramProps) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...bins.map((b) => b.count));
  const first = bins[0]?.from;
  const lastEdge = bins.length ? bins[bins.length - 1].from : undefined;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            fontSize: 'var(--fs-sm)',
            fontWeight: 600,
            color: 'var(--text)',
          }}
        >
          {title}
        </span>
        {soft && (
          <span
            style={{
              fontSize: 'var(--fs-2xs)',
              padding: '1px 6px',
              borderRadius: 999,
              background: 'var(--bg-muted)',
              color: 'var(--text-muted)',
            }}
          >
            soft
          </span>
        )}
      </div>
      {subtitle != null && (
        <div
          style={{
            fontSize: 'var(--fs-2xs)',
            color: 'var(--text-faint)',
            marginBottom: 6,
          }}
        >
          {subtitle}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 3,
          height: barHeight,
        }}
      >
        {bins.map((b, i) => {
          const active = emphasizeAll || b.inRange;
          const range = `${b.from}${b.to == null ? '+' : `–${b.to}`}`;
          return (
            // Full-height column so the whole area is hoverable (thin bars are
            // hard to hit) and the tooltip sits at a consistent top position.
            <div
              key={i}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover((h) => (h === i ? null : h))}
              style={{
                position: 'relative',
                flex: 1,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                cursor: 'default',
              }}
            >
              {hover === i && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 4px)',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                    padding: '2px 6px',
                    borderRadius: 4,
                    background: 'var(--text)',
                    color: 'var(--bg-canvas)',
                    fontSize: 'var(--fs-2xs)',
                    fontVariantNumeric: 'tabular-nums',
                    pointerEvents: 'none',
                    zIndex: 2,
                    boxShadow: 'var(--shadow-sm)',
                  }}
                >
                  {range}
                  {unit ? ` ${unit}` : ''} · <b>{b.count}</b>
                </div>
              )}
              <div
                style={{
                  height: `${Math.max(2, (b.count / max) * 100)}%`,
                  background: active ? 'var(--accent)' : 'var(--text-faint)',
                  opacity: active ? 1 : 0.5,
                  outline:
                    hover === i
                      ? '1px solid var(--accent)'
                      : '1px solid transparent',
                  borderRadius: '3px 3px 0 0',
                  transition: 'height .2s',
                }}
              />
            </div>
          );
        })}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 'var(--fs-2xs)',
          color: 'var(--text-faint)',
          marginTop: 4,
        }}
      >
        <span>{first}</span>
        <span>
          {lastEdge}
          {unit ? ` ${unit}` : ''}
        </span>
      </div>
      {stat != null && (
        <div
          style={{
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-secondary)',
            marginTop: 6,
          }}
        >
          {stat}
        </div>
      )}
    </div>
  );
}
